import EntitlementWarning from '../../core/EntitlementWarning';
import Errored from '../../core/Errored';
import Grid from '@material-ui/core/Grid';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import { v4 as uuidv4 } from 'uuid';
import { ENTITLEMENTS } from '../../application-core/constants';
import HistoryViewer from '../../core/HistoryViewer';
import GS from '../../grafsync/grafsync';
import LangConfirmationField from '../../core/LangConfirmationField';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Avatar from '@material-ui/core/Avatar';
import { globalStyles } from '../../core/style/gstheme';
import { withStyles } from '@material-ui/core/styles';
import withGS from '../withGShoc';
import Chip from '@material-ui/core/Chip';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import PersonIcon from '@material-ui/icons/Person';
import WorkIcon from '@material-ui/icons/Work';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import HelpIcon from '@material-ui/icons/Help';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import MoodIcon from '@material-ui/icons/Mood';
import UpdateIcon from '@material-ui/icons/Update';
import MoodBadIcon from '@material-ui/icons/MoodBad';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { UserConsumer } from '../providers/UserProvider';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AssignUserDialog from './AssignUserDialog';
import { STATUS_DRAFT, STATUS_REJECTED, STATUS_ACCEPTED, STATUS_PENDING } from '../../core/utils/constants';
import { Typography } from '@material-ui/core';
import { getLabelDisplayLangFromLabel, getLabelTextFromLabel, makeCancelable } from '../../core/utils/utils';
import StatusTimelineViewer from './StatusTimelineViewer';
import RejectResourceDialog from './RejectResourceDialog';
import TasksViewer from './TasksViewer';

const DRAFT = STATUS_DRAFT;
const REJECTED = STATUS_REJECTED;
const APPROVED = STATUS_ACCEPTED;
const PENDING = STATUS_PENDING;

const styles = () => {
  return Object.assign({
    count: {
      backgroundColor: '#CA4F0C',
      borderRadius: '50%',
      color: 'white',
      transform: 'translate(36px, -10px)',
      fontSize: '12px',
      display: 'inline-block',
      paddingTop: '2px',
      width: '20px',
      textAlign: 'center',
      verticalAlign: 'middle',
      height: '16px'
    }
  }, globalStyles);
};

class WorkflowResourceManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showAssignUserDialog: false,
      showRejectResourceDialog: false,
      showTasks: false,
      showStatusTimeline: false,
      showHistorySinceStatusChange: false,
      statusChangesHistory: [],
      statusChangeTimestamp: undefined,
      completionCount: 0,
      isErrored: undefined,
      user: undefined
    };
    this.cancellable = null;
  }

  toggleStatusTimeline = async () => {
    if (this.state.showStatusTimeline === true) {
      this.setState({ showStatusTimeline: false });
      return;
    }
    if (this.state.statusChangesHistory.length === 0) {
      await this.loadStatusTimeline();
      if (this.cancellable && this.cancellable.isCancelled()) return;
    }
    this.setState({ showStatusTimeline: true, showTasks: false, showHistorySinceStatusChange: false });
  };

  toggleHistorySinceStatusChange = async () => {
    if (this.state.showHistorySinceStatusChange === true) {
      this.setState({ showHistorySinceStatusChange: false });
      return;
    }
    if (this.state.statusChangesHistory.length === 0) {
      await this.loadStatusTimeline();
      if (this.cancellable && this.cancellable.isCancelled()) return;
    }
    this.setState({ showStatusTimeline: false, showTasks: false, showHistorySinceStatusChange: true });
  };

  showRejectResource = () => this.setState({ showRejectResourceDialog: true });

  showAssignUser = () => this.setState({ showAssignUserDialog: true });

  showTaskViewer = (showTasks) => this.setState({ showStatusTimeline: false, showTasks, showHistorySinceStatusChange: false });

  cancelAssignUser = () => this.setState({ showAssignUserDialog: false });

  computeLabel = () => {
    const data = this.props.data;
    const labelProp = data.label || data.prefLabel || data.literalForm;
    const displayLang = getLabelDisplayLangFromLabel(labelProp, this.props.defaultLang, this.props.defaultLang);
    const label = getLabelTextFromLabel(labelProp, data.id, displayLang, data.id);
    return label;
  };

  createTask = (user, assignedUser, text, lang, notify) => {
    try {
      this.setState({ showCreateTaskDialog: false }, async () => {
        try {
          const iriID = uuidv4();
          const itemIRI = "https://grafsync.graphifi.com/data/task/" + iriID;
          // Inverse will add this to the concept
          let newItem = {
            id: itemIRI,
            type: "Task",
            isTaskFor: this.props.id,
            user,
            status: [PENDING],
            ...(assignedUser !== "All") && { assignedUser },
            assignedNote: { [lang]: text }
          };
          const label = this.computeLabel();
          const notification = {
            "user": user,
            "target": this.props.id,
            "targetClass": this.props.resourceClass,
            "component": this.props.componentIRI,
            "instructions": "You have been assigned a task for a resource (" + label + ")."
          };
          GS.handle("create-task");
          if (notify) {
            GS.setNotification(notification);
          }
          await GS.insertResource(newItem, itemIRI, this.props.graph);
          const discussionID = uuidv4();
          const discussionIRI = "https://grafsync.graphifi.com/data/discussion/" + discussionID;
          // Inverse will add this to the task
          let newDiscussion = {
            id: discussionIRI,
            isDiscussionOf: itemIRI,
            type: "Discussion"
          };
          await GS.insertResource(newDiscussion, discussionIRI, this.props.graph);
        }
        catch (e) {
          if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  assignUser = (user, text, lang, notify = false) => {
    try {
      if (user === "All") user = undefined;
      const data = this.props.data;
      this.setState({ showAssignUserDialog: false, user });
      GS.handle("assign-user");
      const label = this.computeLabel();
      const notification = {
        "user": user,
        "target": this.props.id,
        "targetClass": this.props.resourceClass,
        "component": this.props.componentIRI,
        "instructions": "You have been assigned to manage workflow for a resource (" + label + ")."
      };
      if (!(data.assignedUser === undefined && user === undefined)) {
        if (data.assignedUser !== undefined) {
          if (user === undefined) {
            // Assign it to 'All' by removing it
            this.props.onDelete('assignedUser', data.assignedUser);
          }
          else {
            // Might assign again to same user with a different note, or
            // Just in case somebody else has assigned it to 'user' in the meantime don't do anything
            if (data.assignedUser !== user) {
              if (notify) {
                GS.setNotification(notification);
              }
              this.props.onUpdate('assignedUser', user);
            }
          }
        }
        else {
          // Wasn't assigned before so assign now
          if (notify) {
            GS.setNotification(notification);
          }
          this.props.onAdd('assignedUser', user);
        }
      }
      let valueExistsAlready = false;
      const langExistsAlready = data.assignedNote && data.assignedNote[lang];
      if (langExistsAlready) {
        const langValues = Array.isArray(data.assignedNote[lang]) ?
          data.assignedNote[lang]
          :
          [data.assignedNote[lang]];
        valueExistsAlready = langValues.includes(text);
      }
      if (!valueExistsAlready && text !== "") {
        this.props.onAdd('assignedNote', text, lang);
      }
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  addRejectionNote = (text = "", lang) => {
    try {
      this.setState({ showRejectResourceDialog: false });
      let valueExistsAlready = false;
      const langExistsAlready = this.props.data.rejectedNote && this.props.data.rejectedNote[lang];
      GS.handle("add-rejection-note");
      if (langExistsAlready) {
        const langValues = Array.isArray(this.props.data.rejectedNote[lang]) ?
          this.props.data.rejectedNote[lang]
          :
          [this.props.data.rejectedNote[lang]];
        valueExistsAlready = langValues.includes(text);
      }
      if (!valueExistsAlready && text !== "") {
        this.props.onAdd('rejectedNote', text, lang);
      }
      this.setToStatus(REJECTED);
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  setToStatus = (status) => {
    this.props.data.status !== undefined ?
      this.props.onUpdate('status', status, null, 0)
      :
      this.props.onAdd('status', status);
    this.setState({
      statusChangesHistory: [],
      showStatusTimeline: false,
      showHistorySinceStatusChange: false
    });
  };

  getStatusTimeline = () => {
    this.cancellable = makeCancelable(GS.statusTimeline(this.props.id, this.props.graph));
    return this.cancellable;
  };

  loadStatusTimeline = async () => {
    try {
      GS.handle("load-status-timeline");
      const statusChangesHistory = await this.getStatusTimeline().promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      const statusChangeTimestamp = statusChangesHistory.length > 0 ?
        statusChangesHistory[0].timeOfChange
        :
        undefined;
      this.setState({ statusChangesHistory, statusChangeTimestamp });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  removeNote = (language, value) => GS.deleteProperty(this.props.id, this.props.graph, "assignedNote", value, language);

  removeRejectionNote = (language, value) => GS.deleteProperty(this.props.id, this.props.graph, "rejectedNote", value, language);

  updateCompletionCount = (completedTasks) => this.setState({ completionCount: completedTasks.length });

  render() {

    const { showAssignUserDialog, statusChangesHistory, showStatusTimeline, showRejectResourceDialog,
      showHistorySinceStatusChange, statusChangeTimestamp, showTasks, completionCount, isErrored } = this.state;
    const { classes, status, disabled, project, projectUsers, assignedUser, defaultLang, entitlements, resourceClass, componentIRI,
      assignedNote, rejectedNote, id, graph, data } = this.props;

    if (isErrored) return <Errored dataTest="wm-error" data={isErrored} />;

    const usedStatus = status ? status[0] : null;
    const user = projectUsers.find(user => user.value === assignedUser);
    const isReadOnly = !entitlements.includes(ENTITLEMENTS.EDIT_TAXONOMY);
    const remainingTaskCount = data.hasTask ? data.hasTask.length - completionCount : 0;
    const assigned = assignedUser ?
      user ?
        user.label
        :
        "User not available"
      :
      "Unassigned";
    const statusText = usedStatus ?
      usedStatus === DRAFT ?
        "Draft"
        :
        usedStatus === APPROVED ?
          "Approved"
          :
          "Rejected"
      :
      "Uncategorised";

    const assignedNotesExist = assignedNote ?
      Object.keys(assignedNote).some(lang => {
        const noteItems = Array.isArray(assignedNote[lang]) ? assignedNote[lang] : [assignedNote[lang]];
        return noteItems.some(ni => ni !== "");
      })
      :
      false;

    const rejectionReasonsExist = rejectedNote ?
      Object.keys(rejectedNote).some(lang => {
        const noteItems = Array.isArray(rejectedNote[lang]) ? rejectedNote[lang] : [rejectedNote[lang]];
        return noteItems.some(ni => ni !== "");
      })
      :
      false;

    const draftAvailable = !data.hasTask || data.hasTask.length === 0;

    return (
      <UserConsumer>
        {({ user }) => {
          return (
            <div data-test="workflow-resource-manager">
              <div style={{
                backgroundColor: 'rgb(1, 87, 155, 0.6)',
                padding: 12,
                borderRadius: 4,
                color: 'white'
              }}>
                <Grid container spacing={0} classes={
                  {
                    "spacing-xs-2": this.props.classes.fullGrid
                  }}>
                  <Grid item xs={12} md={6}>
                    <div style={{ display: 'flex', width: '100%' }}>
                      <div style={{ flex: 0 }}>
                        <Chip
                          data-test="status-chip"
                          color="primary"
                          avatar={
                            <Avatar style={{ backgroundColor: 'inherit' }}>
                              {(!status || status === undefined || status.length === 0) &&
                                <HelpIcon data-test="uncategorised-icon" fontSize="small" />
                              }
                              {usedStatus === DRAFT &&
                                <NewReleasesIcon data-test="draft-icon" fontSize="small" />
                              }
                              {usedStatus === APPROVED &&
                                <MoodIcon data-test="approved-icon" fontSize="small" />
                              }
                              {usedStatus === REJECTED &&
                                <MoodBadIcon data-test="rejected-icon" fontSize="small" />
                              }
                            </Avatar>
                          }
                          clickable={false}
                          label={statusText}
                          className={classes.statusChip}
                        />
                      </div>
                      {usedStatus !== APPROVED && usedStatus !== REJECTED &&
                        <div style={{ marginLeft: 24, flex: 0 }}>
                          <Chip
                            data-test="assignment-chip"
                            color="secondary"
                            avatar={
                              <Avatar style={{ backgroundColor: 'inherit' }}>
                                {assignedUser ?
                                  <AssignmentIndIcon data-test="assigned-icon" fontSize="small" />
                                  :
                                  <PersonOutlineIcon data-test="unassigned-icon" fontSize="small" />
                                }
                              </Avatar>
                            }
                            clickable={false}
                            label={assigned}
                            className={classes.statusChip}
                          />
                        </div>
                      }
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {!disabled &&
                      <div style={{ textAlign: 'right' }}>
                        {(!usedStatus || usedStatus === APPROVED || usedStatus === REJECTED)
                          && entitlements.includes(ENTITLEMENTS.MANAGE_WORKFLOW) && draftAvailable &&
                          <span style={{ marginRight: 12 }}>
                            <Tooltip placement="right" title='Set to Draft'>
                              <IconButton aria-label="Set to draft" data-test="set-to-draft" size="small" onClick={() => this.setToStatus(DRAFT)}>
                                <NewReleasesIcon style={{ color: 'white' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        }
                        {entitlements.includes(ENTITLEMENTS.MANAGE_WORKFLOW) &&
                          (!usedStatus || usedStatus === DRAFT) &&
                          <span>
                            <span>
                              <Tooltip placement="right" title={remainingTaskCount > 0 ? 'All tasks must be completed to approve' : 'Approve'}>
                                <span>
                                  <IconButton
                                    aria-label="Approve resource"
                                    disabled={remainingTaskCount > 0}
                                    data-test="approve-resource"
                                    size="small"
                                    onClick={() => this.setToStatus(APPROVED)}>
                                    <ThumbUpIcon style={{ color: remainingTaskCount > 0 ? '#c0c0c0' : 'white' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </span>
                            <span style={{ marginLeft: 12 }}>
                              <Tooltip placement="right" title='Reject'>
                                <IconButton aria-label="Reject" data-test="reject-resource" size="small" onClick={this.showRejectResource}>
                                  <ThumbDownIcon style={{ color: 'white' }} />
                                </IconButton>
                              </Tooltip>
                            </span>
                          </span>
                        }
                        {entitlements.includes(ENTITLEMENTS.ASSIGN_WORKFLOW) && usedStatus === DRAFT &&
                          <span style={{ marginLeft: 12 }}>
                            <Tooltip placement="right" title='Assign'>
                              <IconButton aria-label="Assign" data-test="assign" size="small" onClick={this.showAssignUser}>
                                <PersonIcon style={{ color: 'white' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        }
                        {(entitlements.includes(ENTITLEMENTS.CREATE_TASK)
                          || entitlements.includes(ENTITLEMENTS.EDIT_TASK)
                          || entitlements.includes(ENTITLEMENTS.VIEW_TASK)) &&
                          <span style={{ marginLeft: data.hasTask && data.hasTask.length > 0 && remainingTaskCount > 0 ? -12 : 12 }}>
                            {data.hasTask && data.hasTask.length > 0 && remainingTaskCount > 0 &&
                              <span data-test="completion-count" className={classes.count}>{remainingTaskCount}</span>
                            }
                            <Tooltip placement="right" title='Show/hide tasks'>
                              <span>
                                <IconButton aria-label="Show tasks" data-test="show-tasks" size="small" onClick={() => this.showTaskViewer(!showTasks)}>
                                  <WorkIcon style={{ color: 'white' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </span>
                        }
                        <span style={{ marginLeft: 12 }}>
                          <Tooltip placement="right" title='Status changes timeline'>
                            <IconButton aria-label="Status changes timeline" data-test="status-changes" size="small" onClick={this.toggleStatusTimeline}>
                              <AccessTimeIcon style={{ color: 'white' }} />
                            </IconButton>
                          </Tooltip>
                        </span>
                        <span style={{ marginLeft: 12 }}>
                          <Tooltip placement="right" title='Updates since last state change'>
                            <IconButton aria-label="Updates since last state change" data-test="updates" size="small" onClick={this.toggleHistorySinceStatusChange}>
                              <UpdateIcon style={{ color: 'white' }} />
                            </IconButton>
                          </Tooltip>
                        </span>
                      </div>
                    }
                  </Grid>
                </Grid>
                {showAssignUserDialog && !disabled &&
                  <AssignUserDialog
                    onAssignUser={this.assignUser}
                    existingNotes={assignedNote}
                    project={project}
                    defaultLang={defaultLang}
                    projectUsers={projectUsers}
                    onCancel={this.cancelAssignUser}
                  />
                }
                {showRejectResourceDialog && !disabled &&
                  <RejectResourceDialog
                    addRejectionNote={this.addRejectionNote}
                    existingNotes={rejectedNote}
                    project={project}
                    defaultLang={defaultLang}
                  />
                }
                {
                  user.id === assignedUser && !entitlements.includes(ENTITLEMENTS.MANAGE_WORKFLOW) &&
                  <div style={{ marginTop: 12, marginBottom: 12 }}>
                    <EntitlementWarning
                      compact={true}
                      border={true}
                      message="You do not have permissions to manage workflow."
                      requiredEntitlements={[
                        ENTITLEMENTS.MANAGE_WORKFLOW
                      ]} />
                  </div>
                }
                {!disabled && usedStatus !== REJECTED && (!assignedUser || user.id === assignedUser)
                  && assignedNotesExist && Object.keys(assignedNote).length > 0 &&
                  <div style={{
                    marginTop: 12,
                    marginBottom: 12
                  }}>
                    {user.id === assignedUser ?
                      <Typography data-test="instructions-you" variant="body2" style={{ marginBottom: 6 }}>Instructions for You</Typography>
                      :
                      <Typography data-test="instructions" variant="body2" style={{ marginBottom: 6 }}>Instructions</Typography>
                    }
                    {Object.keys(assignedNote).sort().map((lang) => {
                      const noteItems = Array.isArray(assignedNote[lang]) ? [...assignedNote[lang]] : [assignedNote[lang]];
                      let positions = {};
                      noteItems.forEach((note, i) => positions[note] = i);
                      return <div key={lang}>
                        {noteItems
                          .sort((a, b) => {
                            if (a.toLowerCase() < b.toLowerCase()) return -1;
                            if (a.toLowerCase() > b.toLowerCase()) return 1;
                            return 0;
                          })
                          .map((note, noteIndex) =>
                            <LangConfirmationField
                              dataTest={"note-" + lang + "-" + noteIndex}
                              focus={false}
                              key={lang + "-" + noteIndex}
                              canBeDeleted={usedStatus === DRAFT}
                              multiline={true}
                              text={note}
                              lang={lang}
                              onDelete={() => this.removeNote(lang, note)}
                              isReadOnly={isReadOnly} />
                          )}
                      </div>;
                    })}
                  </div>
                }
                {usedStatus !== APPROVED && rejectionReasonsExist &&
                  <div
                    data-test="rejection-area"
                    style={{
                      marginTop: 12,
                      marginBottom: 12
                    }}>
                    <Typography data-test="rejection-reason" variant="body2" style={{ marginBottom: 6 }}>Reason for Rejection</Typography>
                    {Object.keys(rejectedNote).sort().map((lang) => {
                      const noteItems = Array.isArray(rejectedNote[lang]) ? rejectedNote[lang] : [rejectedNote[lang]];
                      let positions = {};
                      noteItems.forEach((note, i) => positions[note] = i);
                      return <div key={lang}>
                        {noteItems
                          .sort((a, b) => {
                            if (a.toLowerCase() < b.toLowerCase()) return -1;
                            if (a.toLowerCase() > b.toLowerCase()) return 1;
                            return 0;
                          })
                          .map((note, noteIndex) =>
                            <LangConfirmationField
                              focus={false}
                              dataTest={"note-" + lang + "-" + noteIndex}
                              key={lang + "-" + noteIndex}
                              canBeDeleted={usedStatus === DRAFT}
                              multiline={true}
                              text={note}
                              lang={lang}
                              onDelete={() => this.removeRejectionNote(lang, note)}
                              isReadOnly={isReadOnly} />
                          )}
                      </div>;
                    })}
                  </div>
                }
                {!disabled && showStatusTimeline &&
                  <div style={{ backgroundColor: 'white', paddingLeft: 12, paddingRight: 12, paddingTop: 2, paddingBottom: 12, borderRadius: 4, marginTop: 12 }}>
                    <StatusTimelineViewer history={statusChangesHistory} />
                  </div>
                }
                {!disabled &&
                  <div style={{
                    ...(showTasks) && {
                      backgroundColor: 'white',
                      overflowX: 'scroll',
                      paddingLeft: 12,
                      paddingRight: 12,
                      paddingTop: 2,
                      paddingBottom: 12,
                      borderRadius: 4,
                      marginTop: 12
                    }
                  }}>
                    <TasksViewer
                      projectUsers={projectUsers}
                      resourceIRI={id}
                      resourceClass={resourceClass}
                      componentIRI={componentIRI}
                      visible={showTasks}
                      defaultLang={defaultLang}
                      disabled={usedStatus !== DRAFT}
                      currentUser={user.id}
                      manager={assignedUser}
                      onUpdateCompletionCount={this.updateCompletionCount}
                      allowTaskCreation={usedStatus === DRAFT && assignedUser ? true : false}
                      onCreateTask={this.createTask}
                      entitlements={entitlements}
                      tasks={data.hasTask}
                      graph={graph} />
                  </div>
                }
                {!disabled && showHistorySinceStatusChange &&
                  <div style={{
                    backgroundColor: 'white',
                    overflowX: 'scroll',
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingTop: 2,
                    paddingBottom: 12,
                    marginTop: 12,
                    borderRadius: 4
                  }}>
                    <HistoryViewer
                      handleNotAvailable={true}
                      displayFilters={false}
                      forceStartDate={true}
                      compactNoEdits={true}
                      suppressLoading={true}
                      title="History Since Last Status Change"
                      startTimestamp={statusChangeTimestamp}
                      id={id}
                      graph={graph} />
                  </div>
                }
              </div>
            </div>
          );
        }}
      </UserConsumer>
    );
  }
}

WorkflowResourceManager.defaultProps = {
  disabled: false,
  assignUser: undefined
};

WorkflowResourceManager.propTypes = {
  id: PropTypes.string,
  disabled: PropTypes.bool,
  status: PropTypes.array,
  defaultLang: PropTypes.string.isRequired,
  entitlements: PropTypes.array,
  assignedUser: PropTypes.string,
  assignedNote: PropTypes.object,
  rejectedNote: PropTypes.object,
  resourceClass: PropTypes.string,
  componentIRI: PropTypes.string,
  projectUsers: PropTypes.array,
  project: PropTypes.string.isRequired,
  graph: PropTypes.string
};

export default withStyles(styles)(withGS(WorkflowResourceManager));